import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibField } from '@chewy/kib-fields-new-react'
import { Grid, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import * as R from 'ramda'
import {
  DateUtils,
  ErrorTooltip,
  LanguageUtils,
  NumberUtils,
  PuiTextField,
  useFields,
} from '@pbt/pbt-ui-components'

import Features from '../../../constants/features'
import {
  MembershipSignUpFlow,
  POS_MEMBERSHIP_FLOW_COMPLETE_MESSAGE_TYPE,
} from '../../../constants/wellnessPlansConstants'
import { getCurrentBusiness } from '../../../store/duck/businesses'
import {
  clearEmailVerifyData,
  editClient,
  getClientEmailVerifyData,
  getClientsError,
  getClientsIsLoading,
  getCurrentClient,
  verifyClientEmailForBoop,
} from '../../../store/duck/clients'
import { getFeatureToggle } from '../../../store/duck/constants'
import {
  getAcceptedTcVersionId,
  getMembershipActiveSignUpFlow,
  getMembershipIsFreeSignUp,
  getMembershipIsPosFlow,
  getMembershipSelection,
  getMembershipUrlToken,
} from '../../../store/duck/wellnessPlans'
import useCallbackWhenLoaded from '../../../utils/useCallbackWhenLoaded'
import KioskScreen from '../KioskScreen'
import useHandleFreeSignUp from './useHandleFreeSignUp'
import { useKioskMembershipEnrollmentTracking } from './useKioskMembershipEnrollmentTracking'
import { useMembershipSignupChargeLabels } from './wellnessPlanUtils'

import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'

const useStyles = makeStyles(
  (theme) => ({
    textField: {
      maxWidth: 382,
      marginTop: theme.spacing(2),
    },
    title: {
      textAlign: 'left',
      paddingLeft: theme.spacing(4),
    },
    content: {
      padding: theme.spacing(2, 4),
      [theme.breakpoints.down('sm')]: {
        padding: 0,
      },
    },
    amountText: {
      color: R.prop('buttonsColor') || theme.colors.alertLabelError,
      fontWeight: 500,
      fontSize: '1.8rem',
    },
    labelContainer: {
      marginRight: theme.spacing(1),
    },
  }),
  { name: 'MembershipEmailVerificationScreen' },
)

const MembershipEmailVerificationScreen = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Membership'])

  const client = useSelector(getCurrentClient)
  const clientsError = useSelector(getClientsError)
  const clientsIsLoading = useSelector(getClientsIsLoading)
  const activeSignUpFlow = useSelector(getMembershipActiveSignUpFlow)
  const emailVerifyData = useSelector(getClientEmailVerifyData)
  const isFreeSignUp = useSelector(getMembershipIsFreeSignUp)
  const isIpoM2bBraintreeSignupEnabled = useSelector(
    getFeatureToggle(Features.IPO_M2B_BRAINTREE_SIGNUP),
  )
  const isMembershipSignupTerminalEnabled = useSelector(
    getFeatureToggle(Features.MEMBERSHIP_SIGNUP_TERMINAL),
  )

  const currentBusiness = useSelector(getCurrentBusiness)
  const isPosMembershipFlow = useSelector(getMembershipIsPosFlow)
  const membershipUrlToken = useSelector(getMembershipUrlToken)
  const selection = useSelector(getMembershipSelection)
  const { amount, hasSelectedAutorenewalPlans, renewDate } =
    useMembershipSignupChargeLabels()
  const isBusinessOmniChannel = currentBusiness?.omniChannel
  const shouldUseBraintree =
    isIpoM2bBraintreeSignupEnabled && isBusinessOmniChannel
  const tcVersionId = useSelector(getAcceptedTcVersionId)

  const { handler: handleFreeSignUp, isLoading: signUpIsLoading } =
    useHandleFreeSignUp()
  const { trackMembershipEnrollment } = useKioskMembershipEnrollmentTracking()
  const [isSubmittingPosMembershipFlow, setIsSubmittingPosMembershipFlow] =
    useState(false)
  const [pendingUpdatedEmail, setPendingUpdatedEmail] = useState(null)

  const { customColorsEnabled, buttonsColor } = currentBusiness || {}
  const classes = useStyles({
    buttonsColor: customColorsEnabled ? buttonsColor : undefined,
  })

  const clientName = LanguageUtils.capitalize(client?.firstName || 'client')
  const showPosMembershipFlow =
    isPosMembershipFlow && isMembershipSignupTerminalEnabled
  const isLoading =
    clientsIsLoading ||
    signUpIsLoading ||
    (showPosMembershipFlow && isSubmittingPosMembershipFlow)

  const onBeforeBack = () => {
    trackMembershipEnrollment('BACK_CLICKED', {
      stepName: 'confirm_email',
      stepNumber: 7,
    })
  }

  const {
    fields: { email },
    validate,
  } = useFields([
    {
      name: 'email',
      label: t('Common:EMAIL'),
      validators: ['email', 'required'],
      initialValue: client?.email || '',
    },
  ])

  const onClientUpdated = () => {
    if (showPosMembershipFlow) {
      const postMessagePayload = {
        type: POS_MEMBERSHIP_FLOW_COMPLETE_MESSAGE_TYPE,
        selection,
        amount,
        planAutoRenewsOn: DateUtils.formatDate(renewDate),
        hasSelectedAutorenewalPlans,
        tcVersionId,
        updatedEmail: email.value,
        token: membershipUrlToken,
      }
      window?.parent?.postMessage(postMessagePayload, '*') /* NOSONAR */
      return
    }
    if (activeSignUpFlow === MembershipSignUpFlow.HYBRID) {
      const payload = {
        type: 'HANDLE_SEND_HYBRID_SIGN_UP_LINK',
        updatedEmail: email.value,
        tcVersionId,
      }
      window?.parent?.postMessage(payload, '*')
    } else {
      handleFreeSignUp()
    }
  }

  useEffect(() => {
    if (emailVerifyData) {
      dispatch(clearEmailVerifyData())
    }
    setIsSubmittingPosMembershipFlow(false)
    setPendingUpdatedEmail(null)
  }, [email.value])

  useEffect(() => {
    trackMembershipEnrollment('CONFIRM_EMAIL_VIEWED')
  }, [trackMembershipEnrollment])

  useEffect(() => {
    if (!showPosMembershipFlow) {
      return
    }

    if (!pendingUpdatedEmail || clientsIsLoading) {
      return
    }

    if (client?.email === pendingUpdatedEmail) {
      setPendingUpdatedEmail(null)
      onClientUpdated()
      return
    }

    if (clientsError) {
      setPendingUpdatedEmail(null)
      setIsSubmittingPosMembershipFlow(false)
      return
    }

    setPendingUpdatedEmail(null)
    setIsSubmittingPosMembershipFlow(false)
  }, [
    client?.email,
    clientsError,
    clientsIsLoading,
    onClientUpdated,
    pendingUpdatedEmail,
    showPosMembershipFlow,
  ])

  useEffect(() => {
    if (
      showPosMembershipFlow &&
      isSubmittingPosMembershipFlow &&
      !clientsIsLoading &&
      !signUpIsLoading &&
      (emailVerifyData?.valid === false ||
        (clientsError && !pendingUpdatedEmail))
    ) {
      setIsSubmittingPosMembershipFlow(false)
    }
  }, [
    clientsError,
    clientsIsLoading,
    emailVerifyData?.valid,
    isSubmittingPosMembershipFlow,
    pendingUpdatedEmail,
    showPosMembershipFlow,
    signUpIsLoading,
  ])

  const proceedWhenClientWillBeUpdated = useCallbackWhenLoaded(
    onClientUpdated,
    getClientsIsLoading,
  )

  const onVerified = () => {
    if (emailVerifyData?.valid) {
      if (email.value === client.email) {
        onClientUpdated()
      } else {
        const newClient = {
          ...client,
          email: email.value,
          patients: null,
          coparents: null,
        }
        if (showPosMembershipFlow) {
          setPendingUpdatedEmail(email.value)
        } else {
          proceedWhenClientWillBeUpdated()
        }
        dispatch(editClient(newClient))
      }
    }
  }

  const proceedWhenEmailWillBeVerified = useCallbackWhenLoaded(
    onVerified,
    getClientsIsLoading,
  )

  const onProceed = () => {
    if (validate()) {
      trackMembershipEnrollment('CONFIRM_EMAIL_NEXT_CLICKED', {
        additionalProperties: {
          has_updated_email: email.value !== client?.email,
          membership_flow: showPosMembershipFlow ? 'pos_terminal' : 'standard',
        },
      })
      if (showPosMembershipFlow) {
        setIsSubmittingPosMembershipFlow(true)
      }
      proceedWhenEmailWillBeVerified()
      dispatch(
        verifyClientEmailForBoop(client.id, email.value, isBusinessOmniChannel),
      )
    }
  }

  const emailVerificationLabel = t(
    'Membership:THIS_EMAIL_IS_USED_TO_IDENTIFY_CLIENT_ACCOUNT',
    {
      clientName,
    },
  )

  return (
    <KioskScreen
      additionalLabel={
        showPosMembershipFlow &&
        amount > 0 && (
          <Grid
            container
            item
            alignContent="flex-end"
            className={classes.labelContainer}
            direction="column"
          >
            <Typography className={classes.amountText}>
              {t('Common:CARD_WILL_BE_CHARGED_AMOUNT', {
                amount: NumberUtils.formatMoney(amount),
              })}
            </Typography>
            {hasSelectedAutorenewalPlans && (
              <Typography variant="body2">
                {t('Membership:PLAN_AUTO_RENEWS_ON_DATE', {
                  date: DateUtils.formatDate(renewDate),
                })}
              </Typography>
            )}
          </Grid>
        )
      }
      classes={{ content: classes.content, title: classes.title }}
      justifyContent="flex-start"
      proceedButtonDisabled={isLoading}
      proceedButtonLabel={
        isFreeSignUp || showPosMembershipFlow
          ? t('Common:SIGN_UP')
          : t('Common:NEXT_ACTION')
      }
      proceedButtonLoading={isLoading}
      title={t('Common:CONFIRM_CLIENTS_EMAIL', { clientName })}
      onBeforeBack={onBeforeBack}
      onProceed={onProceed}
    >
      <Grid container item direction="column">
        <Typography>{emailVerificationLabel}</Typography>
        <ErrorTooltip
          message={
            Boolean(emailVerifyData.message) && t('Membership:DUPLICATE_EMAIL')
          }
          open={emailVerifyData.valid === false}
        >
          {shouldUseBraintree ? (
            <KibField
              className={classes.textField}
              invalid={!email.valid}
              label={`${email.label}*`}
              value={email.value}
              onChange={(event) => {
                email.setValue(event.target.value)
              }}
            />
          ) : (
            <PuiTextField
              autoFocus
              fullWidth
              className={classes.textField}
              disabled={isLoading}
              field={email}
              inputProps={{ maxLength: 100 }}
              label={`${email.label}*`}
              type="email"
            />
          )}
        </ErrorTooltip>
      </Grid>
    </KioskScreen>
  )
}

export default MembershipEmailVerificationScreen
